import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import styled, { keyframes, css, createGlobalStyle } from "styled-components";
import { connect } from "react-redux";
import { getDiscountsAPI, addToWishlistAPI, removeFromWishlistAPI } from "../../actions";
import Card from "../Shared/Card";

// ─── Theme tokens ─────────────────────────────────────────────────────────────
const T = {
  bg: "#ffffff",
  surface: "rgba(0,0,0,0.03)",
  surfaceHover: "rgba(0,0,0,0.06)",
  border: "rgba(0,0,0,0.1)",
  borderHover: "rgba(250,129,40,0.3)",
  orange: "#fa8128",
  orangeDim: "rgba(250,129,40,0.12)",
  orangeGlow: "rgba(250,129,40,0.05)",
  text: "#1a1a16",
  textMuted: "rgba(20,20,15,0.4)",
  textSub: "rgba(20,20,15,0.6)",
  radius: "14px",
  radiusSm: "8px",
};

const SORTS = ["Popular", "Newest", "Biggest Deal"];

// ─── Helper — normalise whatever shape discounts arrives in ──────────────────
// Redux may hold: null | [] | {results:[]} | [{...}]
function getResults(discounts) {
  if (!discounts) return null;                          // nothing yet
  if (Array.isArray(discounts)) {
    return discounts.length > 0 ? discounts : null;    // plain array from old cache
  }
  if (discounts.results !== undefined) {
    return discounts.results;                          // paginated API shape
  }
  return null;
}

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const grain = keyframes`
  0%,100% { transform: translate(0,0); }
  10%      { transform: translate(-2%,-3%); }
  30%      { transform: translate(3%,-1%); }
  50%      { transform: translate(-1%,2%); }
  70%      { transform: translate(2%,3%); }
  90%      { transform: translate(-3%,1%); }
`;

// ─── Global ───────────────────────────────────────────────────────────────────
const Global = createGlobalStyle`
  body { background: ${T.bg}; color: ${T.text}; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(250,129,40,0.3); border-radius: 4px; }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: ${T.bg};
  font-family: "DM Sans", sans-serif;
  position: relative;
  overflow-x: hidden;
`;

const GrainOverlay = styled.div`
  pointer-events: none;
  position: fixed;
  inset: -50%;
  width: 200%;
  height: 200%;
  opacity: 0.028;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  animation: ${grain} 8s steps(10) infinite;
  z-index: 0;
`;

const AmbientBlob = styled.div`
  pointer-events: none;
  position: fixed;
  top: -200px;
  right: -200px;
  width: 700px;
  height: 700px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(250,129,40,0.07) 0%, transparent 65%);
  z-index: 0;
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px 80px;
  @media (max-width: 600px) { padding: 0 16px 60px; }
`;

// ─── Header ───────────────────────────────────────────────────────────────────
const Header = styled.div`
  padding: 72px 0 0;
  text-align: center;
  // animation: ${fadeUp} 0.35s ease both;
  @media (min-width: 600px) { padding-top: 96px; }
`;

const Headline = styled.h1`
  font-family: "Playfair Display", Georgia, serif;
  font-size: clamp(2.4rem, 5vw, 4rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${T.text};
  text-align: center;
  em { font-style: italic; color: ${T.orange}; }
`;

const Rule = styled.div`
  height: 1px;
  background: ${T.border};
  margin: 36px 0 28px;
`;

// ─── Controls ────────────────────────────────────────────────────────────────
const Controls = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 32px;
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const CatRail = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
  width: 100%;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  padding: 4px 0;
  margin: -4px 0;
  @media (max-width: 480px) {
    width: calc(100% + 32px);
    margin-left: -16px;
    margin-right: -16px;
    padding-left: 16px;
    padding-right: 16px;
  }
  &::-webkit-scrollbar { display: none; }
`;

const Chip = styled.button`
  padding: 7px 16px;
  border-radius: 30px;
  font-size: 0.8rem;
  font-family: "Courier New", monospace;
  letter-spacing: 0.04em;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.18s;
  flex-shrink: 0;
  scroll-snap-align: start;
  border: 1px solid ${({ $active }) => ($active ? T.orange : T.border)};
  background: ${({ $active }) => ($active ? T.orangeDim : "transparent")};
  color: ${({ $active }) => ($active ? T.orange : T.text)};
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  opacity: 1;
  &:hover {
    border-color: ${T.orange};
    color: ${T.orange};
    background: ${T.orangeDim};
  }
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  @media (max-width: 480px) {
    width: 100%;
    flex-shrink: 1;
    flex-wrap: wrap;
  }
`;

const SortSelect = styled.select`
  background: #fff;
  border: 1px solid ${T.border};
  color: ${T.text};
  font-family: "Courier New", monospace;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  padding: 7px 12px;
  border-radius: 30px;
  cursor: pointer;
  outline: none;
  appearance: none;
  transition: border-color 0.2s;
  min-width: 0;
  flex-shrink: 1;
  @media (max-width: 480px) { flex-shrink: 0; }
  &:focus, &:hover { border-color: rgba(250,129,40,0.35); color: ${T.text}; }
  option { background: #ffffff; }
`;

// ─── Results meta ─────────────────────────────────────────────────────────────
const ResultsMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ResultsCount = styled.p`
  font-family: "Courier New", monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  color: ${T.textMuted};
  text-transform: uppercase;
`;

const ActiveFilters = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const FilterPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-family: "Courier New", monospace;
  background: ${T.orangeDim};
  border: 1px solid rgba(250,129,40,0.3);
  color: ${T.orange};
  cursor: pointer;
  &:hover { background: rgba(250,129,40,0.25); }
`;

// ─── Grid ────────────────────────────────────────────────────────────────────
const Grid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
`;

// ─── Skeleton ────────────────────────────────────────────────────────────────
const SkeletonShimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const SkeletonBase = css`
  background: linear-gradient(90deg, #e0e0e0 25%, #c8c8c8 37%, #e0e0e0 63%);
  background-size: 600px 100%;
  animation: ${SkeletonShimmer} 1.4s infinite linear;
  border-radius: 6px;
`;

const SkeletonCard = styled.div`
  background: #f0f0f0;
  border: 1px solid #e0e0e0;
  border-radius: ${T.radius};
  overflow: hidden;
  min-height: 280px;
`;

const SkeletonImg = styled.div`
  ${SkeletonBase};
  height: 190px;
  border-radius: 0;
`;

const SkeletonBody = styled.div`
  padding: 14px 18px 18px;
`;

const SkeletonLine = styled.div`
  ${SkeletonBase};
  height: 14px;
  width: ${({ w }) => w || "80%"};
  margin-bottom: 10px;
`;

const SkeletonThin = styled.div`
  ${SkeletonBase};
  height: 10px;
  width: ${({ w }) => w || "55%"};
  margin-bottom: 8px;
`;

const SkeletonChip = styled.div`
  ${SkeletonBase};
  height: 34px;
  width: ${({ w }) => w || "80px"};
  border-radius: 30px;
  flex-shrink: 0;
`;

// ─── Empty ───────────────────────────────────────────────────────────────────
const Empty = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 80px 20px;
  animation: ${fadeUp} 0.4s ease both;
`;
const EmptyIcon = styled.div`font-size: 3rem; margin-bottom: 16px;`;
const EmptyText = styled.p`
  font-family: "Playfair Display", serif;
  font-size: 1.4rem;
  font-style: italic;
  color: ${T.textSub};
  margin-bottom: 8px;
`;
const EmptySub = styled.p`font-size: 0.85rem; color: ${T.textMuted};`;

// ─── Search bar ───────────────────────────────────────────────────────────────
const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  max-width: 400px;
  @media (max-width: 480px) {
    max-width: none;
    flex: 1 1 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(250,129,40,0.04);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 12px;
  color: ${T.text};
  font-family: "DM Sans", sans-serif;
  font-size: 0.875rem;
  padding: 10px 16px 10px 40px;
  outline: none;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  &::placeholder { color: ${T.textMuted}; }
  &:focus {
    background: #fff;
    border-color: rgba(250,129,40,0.3);
    box-shadow: 0 0 0 3px rgba(250,129,40,0.1), 0 4px 16px rgba(0,0,0,0.06);
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: ${T.textMuted};
  display: flex;
  align-items: center;
  pointer-events: none;
  svg {
    width: 16px;
    height: 16px;
  }
`;

// ─── Main component ───────────────────────────────────────────────────────────
function DiscountsPage(props) {
  const { catId } = useParams();
  const [activeCat, setActiveCat] = useState("All");
  const [sort, setSort] = useState("Popular");
  const [query, setQuery] = useState("");
  const [saved, setSaved] = useState(new Set());
  const [savingId, setSavingId] = useState(null);

  const { getDiscounts, discounts, token, addToWishlist, removeFromWishlist, wishlist } = props;

  // Normalise whatever shape Redux holds into a plain array (or null if not ready)
  const allDiscounts = useMemo(() => getResults(discounts) || [], [discounts]);
  const hasData = useMemo(() => getResults(discounts) !== null, [discounts]);

  // pageLoading: true only while we genuinely have no data yet
  const [pageLoading, setPageLoading] = useState(!hasData);

  // Fetch only if no usable data on mount
  useEffect(() => {
    if (!hasData) {
      getDiscounts();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop loading as soon as usable data appears
  useEffect(() => {
    if (hasData) {
      setPageLoading(false);
    }
  }, [hasData]);

  const cats = useMemo(() => [
    "All",
    ...[...new Set(allDiscounts.flatMap((d) => d.categories.map((c) => c.name)))],
  ], [allDiscounts]);

  // Sync active category from URL param
  useEffect(() => {
    if (catId && allDiscounts.length > 0) {
      const matchingCat = cats.find((cat) => cat.toLowerCase() === catId.toLowerCase());
      if (matchingCat) setActiveCat(matchingCat);
    }
  }, [catId, allDiscounts.length, cats]);

  // Sync saved state from wishlist — handles both {results:[]} and plain array shapes
  useEffect(() => {
    if (wishlist?.results) {
      setSaved(new Set(wishlist.results.map((item) => item.discount?.id ?? item.discount)));
    } else if (Array.isArray(wishlist) && wishlist.length > 0) {
      setSaved(new Set(wishlist.map((item) => item.discount?.id ?? item.discount ?? item.id)));
    }
  }, [wishlist]);

  const loading = pageLoading;

  // Filter + sort
  const filtered = allDiscounts
    .filter((d) => activeCat === "All" || d.categories.some((c) => c.name === activeCat))
    .filter(
      (d) =>
        !query ||
        d.title.toLowerCase().includes(query.toLowerCase()) ||
        d.location.toLowerCase().includes(query.toLowerCase()),
    )
    .sort((a, b) => {
      if (sort === "Popular") return (b.likes || 0) - (a.likes || 0);
      if (sort === "Biggest Deal") return (b.percentage_discount || 0) - (a.percentage_discount || 0);
      return 0;
    });

  const toggleSave = (id) => {
    if (savingId) return;
    const isCurrentlySaved = saved.has(id);
    setSaved((prev) => {
      const next = new Set(prev);
      isCurrentlySaved ? next.delete(id) : next.add(id);
      return next;
    });
    setSavingId(id);
    const action = isCurrentlySaved
      ? removeFromWishlist({ discount_id: id })
      : addToWishlist({ discount_id: id });
    action
      .then(() => setSavingId(null))
      .catch((error) => {
        console.error("Wishlist operation failed:", error);
        setSavingId(null);
        setSaved((prev) => {
          const next = new Set(prev);
          isCurrentlySaved ? next.add(id) : next.delete(id);
          return next;
        });
        alert("Failed to update wishlist. Please try again.");
      });
  };

  return (
    <>
      <Global />
      <Page>
        <GrainOverlay />
        <AmbientBlob />

        <Inner>
          {/* ── Header — always visible ── */}
          <Header>
            <Headline>
              Fresh deals, <em>no wahala.</em>
            </Headline>
          </Header>

          <Rule />

          {/* ── Controls ── */}
          <Controls>
            <CatRail>
              {loading
                ? [90, 70, 80, 65, 75, 60].map((w, i) => (
                    <SkeletonChip key={i} w={`${w}px`} />
                  ))
                : cats.map((cat) => (
                    <Chip
                      key={cat}
                      $active={activeCat === cat}
                      onClick={() => setActiveCat(cat)}
                    >
                      {cat}
                    </Chip>
                  ))}
            </CatRail>

            <RightControls>
              <SearchWrap>
                <SearchIcon>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </SearchIcon>
                <SearchInput
                  placeholder="Search deals..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  disabled={loading}
                />
              </SearchWrap>
              <SortSelect
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                disabled={loading}
              >
                {SORTS.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </SortSelect>
            </RightControls>
          </Controls>

          {/* ── Results meta ── */}
          {!loading && (
            <ResultsMeta>
              <ResultsCount>
                {filtered.length} deal{filtered.length !== 1 ? "s" : ""} found
                {activeCat !== "All" ? ` · ${activeCat}` : ""}
              </ResultsCount>
              {query && (
                <ActiveFilters>
                  <FilterPill onClick={() => setQuery("")}>"{query}" ✕</FilterPill>
                </ActiveFilters>
              )}
            </ResultsMeta>
          )}

          {/* ── Grid ── */}
          <Grid>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i}>
                  <SkeletonImg />
                  <SkeletonBody>
                    <SkeletonLine w="75%" />
                    <SkeletonLine w="55%" />
                    <SkeletonThin w="40%" />
                    <SkeletonThin w="60%" />
                  </SkeletonBody>
                </SkeletonCard>
              ))
            ) : filtered.length === 0 ? (
              <Empty>
                <EmptyIcon>🔎</EmptyIcon>
                <EmptyText>No discounts found.</EmptyText>
                <EmptySub>Try a different category or search term.</EmptySub>
              </Empty>
            ) : (
              filtered.map((d) => (
                <Card
                  key={d.id}
                  discount={d}
                  onSave={toggleSave}
                  isSaved={saved.has(d.id)}
                  isLoading={savingId === d.id}
                  bgColor="light"
                  isLoggedIn={!!token?.access}
                />
              ))
            )}
          </Grid>
        </Inner>
      </Page>
    </>
  );
}

const mapStateToProps = (state) => ({
  discounts: state.discountState.discounts,
  loading: state.discountState.loading,
  user: state.userState.user,
  token: state.userState.token,
  wishlist: state.discountState.wishlist,
});

const mapDispatchToProps = (dispatch) => ({
  getDiscounts: () => dispatch(getDiscountsAPI()),
  addToWishlist: (data) => dispatch(addToWishlistAPI(data)),
  removeFromWishlist: (data) => dispatch(removeFromWishlistAPI(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DiscountsPage);